import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import SkeletonElement from '../../../../components/skeleton-element/skeleton-element';
import TopThreeTile from './components/top-three-tile/top-three-tile';
import { PLP_PAGE_TYPE } from '../../product-list-page-constants';

import {
  useDataLayer,
  useFitment,
  useProductEntryDataLayerValues
} from '../../../../shared/hooks/hooks';
import { TOP_3_POSITIONS, TOP_3_TYPES } from '../../hooks/use-plp-top-three';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import {
  getPriceInfo,
  getProductImage
} from '../../../../shared/utils/product/product';

import {
  PRODUCT_LIST_TOP_3,
  PRODUCT_LIST_TOP_3_BRANDS,
  PRODUCT_LIST_TOP_3_DISPLAYED,
  PRODUCT_LIST_TOP_3_IDS,
  PRODUCT_LIST_TOP_3_MET,
  PRODUCT_LIST_TOP_3_NAMES,
  PRODUCT_LIST_TOP_3_POSITION,
  PRODUCT_LIST_TOP_3_TYPES,
  PRODUCT_POSITION
} from '../../../../shared/constants/data-layer';
import { PRODUCT_IMAGE_FORMATS } from '../../../../shared/constants/product-images';

import './top-three.scss';

function TopThree(props) {
  const { hasFetchedData, shouldDisplay, tiles } = props;
  const { selectedFitment } = useFitment();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { currentPageType } = useDataLayer();

  useEffect(() => {
    if (shouldDisplay && selectedFitment && currentPageType === PLP_PAGE_TYPE) {
      const top3 = [];
      const top3Brands = [];
      const top3Ids = [];
      const top3Names = [];
      const top3Types = [];

      for (let t = 0; t < tiles.length; t++) {
        const tile = tiles[t];
        const { productData, tile: tileType } = tile || {};
        const { brand, code, name } = productData || {};
        const formattedTileType = TOP_3_TYPES[tileType];

        top3.push(brand, null, code, formattedTileType);
        top3Brands.push(brand);
        top3Ids.push(code || null);
        top3Names.push(name || null);
        top3Types.push(formattedTileType || null);
      }

      trackAction('product_list_top3_view', {
        [PRODUCT_LIST_TOP_3]: top3,
        [PRODUCT_LIST_TOP_3_BRANDS]: top3Brands,
        [PRODUCT_LIST_TOP_3_DISPLAYED]: shouldDisplay ? '1' : '0',
        [PRODUCT_LIST_TOP_3_IDS]: top3Ids,
        [PRODUCT_LIST_TOP_3_MET]: 'Success',
        [PRODUCT_LIST_TOP_3_NAMES]: top3Names,
        [PRODUCT_LIST_TOP_3_POSITION]: TOP_3_POSITIONS,
        [PRODUCT_LIST_TOP_3_TYPES]: top3Types
      });
    }
  }, [shouldDisplay, tiles, currentPageType, selectedFitment]);

  if (hasFetchedData && !shouldDisplay) {
    return null;
  }

  return (
    <div styleName="container">
      {!hasFetchedData ? (
        <>
          <SkeletonElement styleName="top-three-skeleton" />
          <SkeletonElement styleName="top-three-skeleton" />
          <SkeletonElement styleName="top-three-skeleton" />
        </>
      ) : (
        tiles
          ?.sort(
            (t1, t2) => (t1?.sequenceNumber || 0) - (t2?.sequenceNumber || 0)
          )
          ?.reduce((acc, t) => {
            if (typeof t.productData !== 'object' || t.productData === null) {
              return acc;
            }

            const {
              brand,
              code,
              mapDisplayRule,
              mapRuleSatisfied,
              name,
              rearProduct,
              recommendationData,
              unitName,
              url
            } = t.productData || {};
            const { rank } = recommendationData || {};
            const priceInfo = getPriceInfo(t.productData);
            const rearPriceInfo = getPriceInfo(rearProduct);
            const pos = TOP_3_POSITIONS[acc.length];
            const priceSubtext =
              mapRuleSatisfied &&
              mapDisplayRule &&
              mapDisplayRule.messageDisplay &&
              mapDisplayRule.mapPriceDisplay
                ? t.productData.mapDisplayRule.message
                : '';

            acc.push(
              <TopThreeTile
                brand={brand}
                detailsLink={url}
                imageUrl={
                  getProductImage(t.productData, {
                    format:
                      process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ ===
                      'true'
                        ? PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL
                        : PRODUCT_IMAGE_FORMATS.LIST,
                    shouldFallBackToFirst: true
                  })?.url
                }
                isTreadwell={rank}
                key={`${code}-${t.tile}`}
                name={name}
                onViewDetailsClick={e =>
                  trackAction(
                    e,
                    process.env.__FF_TOP_THREE_LOWEST_PRICE_TILE__ === 'true'
                      ? {
                          [PRODUCT_LIST_TOP_3_BRANDS]: [brand],
                          [PRODUCT_LIST_TOP_3_IDS]: [code],
                          [PRODUCT_LIST_TOP_3_NAMES]: [name],
                          [PRODUCT_LIST_TOP_3_POSITION]: [pos],
                          [PRODUCT_LIST_TOP_3_TYPES]: [TOP_3_TYPES[t.tile]]
                        }
                      : {
                          ...getProductEntryDataLayerValues(t.productData),
                          [PRODUCT_POSITION]: rearProduct ? [pos, pos] : [pos]
                        }
                  )
                }
                priceInfo={priceInfo}
                priceSubtext={priceSubtext}
                rank={rank}
                rearCode={rearProduct?.code}
                rearPriceInfo={rearPriceInfo}
                type={t.tile}
                unitName={unitName}
              />
            );

            return acc;
          }, [])
      )}
    </div>
  );
}

TopThree.propTypes = {
  hasFetchedData: PropTypes.bool,
  shouldDisplay: PropTypes.bool,
  tiles: PropTypes.any
};

export default TopThree;
